/**
 * =============================================================================
 * Página: /r/:slug
 * Versão: 2.0.0 (Modelo Híbrido de Landing — Fase Landing v1)
 *
 * Descrição: Página pública que processa o clique em um link de afiliado.
 *   Fluxo:
 *     1. Lê slug + UTMs + cookie existente.
 *     2. POSTa para `affiliate-track-click` (registra clique e devolve dados
 *        da campanha + branding do tenant).
 *     3. Decide o que renderizar baseado em `landing_mode`:
 *          - `redirect` → redirect direto para `destination_url` (comportamento legado).
 *          - `landing`  → renderiza a landing hospedada com formulário.
 *          - `builder`  → placeholder "em construção".
 *     4. Em qualquer caso, grava o cookie 1st-party `uaizy_aff` para atribuição.
 * =============================================================================
 */
import { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Loader2, AlertCircle, Construction } from 'lucide-react';
import { createLogger } from '@/lib/logger';
import {
  AffiliateLandingPage,
  type LandingFormField,
} from '@/components/affiliate/landing/AffiliateLandingPage';

const logger = createLogger('AffiliateRedirectPage');

/** Nome do cookie 1st-party usado para atribuição de leads. */
const COOKIE_NAME = 'uaizy_aff';

/** Lê o cookie 1st-party (se existir). */
function readCookie(name: string): string | null {
  if (typeof document === 'undefined') return null;
  const m = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
  return m ? decodeURIComponent(m[2]) : null;
}

/** Grava cookie 1st-party com TTL configurado pelo programa. */
function writeCookie(name: string, value: string, maxAgeSeconds: number) {
  if (typeof document === 'undefined') return;
  document.cookie = `${name}=${encodeURIComponent(value)}; Max-Age=${maxAgeSeconds}; Path=/; SameSite=Lax`;
}

type Status = 'tracking' | 'redirecting' | 'landing' | 'builder' | 'error';

interface TrackResponse {
  destination_url: string | null;
  cookie_id: string;
  cookie_max_age: number;
  /** Token opaco assinado (HMAC) — substitui link_id na v1.1.0. */
  link_token: string;
  landing_mode: 'redirect' | 'landing' | 'builder';
  landing: {
    title: string | null;
    subtitle: string | null;
    hero_image_url: string | null;
    cta_label: string;
    thank_you_title: string;
    thank_you_message: string;
    redirect_after_submit_seconds: number | null;
    form_fields: LandingFormField[];
  };
  tenant: {
    name: string | null;
    logo_url: string | null;
    /** Slug público do tenant — alimenta o branding (cores) na landing. */
    slug?: string | null;
  };
  /** v1.3.0: dados públicos do afiliado (foto + comentário). */
  affiliate?: {
    avatar_url: string | null;
    bio_comment: string | null;
  } | null;
}

export default function AffiliateRedirectPage() {
  const { slug } = useParams<{ slug: string }>();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState<Status>('tracking');
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [trackData, setTrackData] = useState<TrackResponse | null>(null);

  useEffect(() => {
    let cancelled = false;

    async function track() {
      if (!slug) {
        setStatus('error');
        setErrorMessage('Link inválido');
        return;
      }

      try {
        const supabaseUrl = import.meta.env.VITE_SUPABASE_URL as string;
        const anonKey = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY as string;
        const endpoint = `${supabaseUrl}/functions/v1/affiliate-track-click`;

        const existingCookie = readCookie(COOKIE_NAME);
        const payload = {
          slug,
          cookie_id: existingCookie,
          referer: document.referrer || null,
          user_agent: navigator.userAgent,
          utm_source: searchParams.get('utm_source'),
          utm_medium: searchParams.get('utm_medium'),
          utm_campaign: searchParams.get('utm_campaign'),
          utm_content: searchParams.get('utm_content'),
          utm_term: searchParams.get('utm_term'),
        };

        const res = await fetch(endpoint, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            apikey: anonKey,
            Authorization: `Bearer ${anonKey}`,
          },
          body: JSON.stringify(payload),
        });

        if (cancelled) return;

        if (!res.ok) {
          const body = await res.json().catch(() => ({} as { error?: string }));
          const msg =
            res.status === 404
              ? 'Este link não existe ou foi removido.'
              : res.status === 410
              ? 'Este link não está mais ativo.'
              : (body as { error?: string }).error ||
                'Não foi possível processar o link.';
          setErrorMessage(msg);
          setStatus('error');
          return;
        }

        const data = (await res.json()) as TrackResponse;

        // Renova/cria cookie 1st-party de atribuição (sempre, em qualquer modo).
        writeCookie(COOKIE_NAME, data.cookie_id, data.cookie_max_age);
        setTrackData(data);

        // Decide o próximo passo conforme o modo da campanha
        const mode = data.landing_mode ?? 'redirect';

        if (mode === 'landing') {
          setStatus('landing');
        } else if (mode === 'builder') {
          setStatus('builder');
        } else {
          // Modo redirect (default): mantém comportamento legado
          if (!data.destination_url) {
            setErrorMessage('Esta campanha não possui URL de destino configurada.');
            setStatus('error');
            return;
          }
          setStatus('redirecting');
          window.location.replace(data.destination_url);
        }
      } catch (err) {
        if (cancelled) return;
        logger.error('Falha no tracking de clique', err);
        setErrorMessage('Não foi possível processar o link.');
        setStatus('error');
      }
    }

    track();
    return () => {
      cancelled = true;
    };
  }, [slug, searchParams]);

  // ============== Render: landing hospedada ==============
  if (status === 'landing' && trackData) {
    return (
      <AffiliateLandingPage
        linkToken={trackData.link_token}
        cookieId={trackData.cookie_id}
        formFields={trackData.landing.form_fields}
        branding={{
          tenantName: trackData.tenant.name,
          tenantLogoUrl: trackData.tenant.logo_url,
          tenantSlug: trackData.tenant.slug ?? null,
          title: trackData.landing.title,
          subtitle: trackData.landing.subtitle,
          heroImageUrl: trackData.landing.hero_image_url,
          ctaLabel: trackData.landing.cta_label,
          thankYouTitle: trackData.landing.thank_you_title,
          thankYouMessage: trackData.landing.thank_you_message,
          // v1.3.0: foto + comentário públicos do afiliado (humanização da landing).
          affiliateAvatarUrl: trackData.affiliate?.avatar_url ?? null,
          affiliateBioComment: trackData.affiliate?.bio_comment ?? null,
        }}
        utms={{
          utm_source: searchParams.get('utm_source'),
          utm_medium: searchParams.get('utm_medium'),
          utm_campaign: searchParams.get('utm_campaign'),
          utm_content: searchParams.get('utm_content'),
          utm_term: searchParams.get('utm_term'),
        }}
      />
    );
  }

  // ============== Render: builder em construção ==============
  if (status === 'builder') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-6">
        <div className="text-center space-y-4 max-w-md">
          <div className="mx-auto h-12 w-12 rounded-full bg-muted flex items-center justify-center">
            <Construction className="h-6 w-6 text-muted-foreground" />
          </div>
          <h1 className="text-lg font-semibold">Página em construção</h1>
          <p className="text-sm text-muted-foreground">
            Esta campanha está usando o modo Builder visual, que ainda será disponibilizado.
          </p>
        </div>
      </div>
    );
  }

  // ============== Render: erro / loader ==============
  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-6">
      <div className="text-center space-y-4 max-w-md">
        {status === 'error' ? (
          <>
            <div className="mx-auto h-12 w-12 rounded-full bg-destructive/10 flex items-center justify-center">
              <AlertCircle className="h-6 w-6 text-destructive" />
            </div>
            <h1 className="text-lg font-semibold">Link indisponível</h1>
            <p className="text-sm text-muted-foreground">{errorMessage}</p>
          </>
        ) : (
          <>
            <Loader2 className="h-8 w-8 mx-auto animate-spin text-primary" />
            <h1 className="text-base font-medium">
              {status === 'redirecting' ? 'Redirecionando…' : 'Carregando…'}
            </h1>
            <p className="text-xs text-muted-foreground">
              Você será encaminhado em instantes.
            </p>
          </>
        )}
      </div>
    </div>
  );
}
